/**
 * Phase 19 / Task 19.3 (DEC-325) — daily BBL trend, this period vs the comparison
 * period, overlaid by day number. One axis (BBL). Two series: the current period
 * is a solid accent line, the comparison a dashed neutral line, so the two are
 * told apart by line style as well as colour; a legend names both. A crosshair
 * tooltip shows each day's real date for both periods, and a table of the same
 * numbers sits behind "Show the numbers".
 */

import * as React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TrendPoint } from './use-management-dashboard';
import { formatBbl, formatDay } from './dashboard-format';

const CURRENT = 'var(--color-accent-blue)';
const PREVIOUS = 'var(--color-text-tertiary)';

interface Row {
  readonly n: number;
  readonly currentDay: string | null;
  readonly previousDay: string | null;
  readonly current: number | null;
  readonly previous: number | null;
  readonly currentBbl: string | null;
  readonly previousBbl: string | null;
}

/** Overlay the two periods by day number; the shorter one simply ends earlier. */
export function overlay(current: readonly TrendPoint[], previous: readonly TrendPoint[]): Row[] {
  const len = Math.max(current.length, previous.length);
  const rows: Row[] = [];
  for (let i = 0; i < len; i += 1) {
    const c = current[i];
    const p = previous[i];
    rows.push({
      n: i + 1,
      currentDay: c?.day ?? null,
      previousDay: p?.day ?? null,
      current: c === undefined ? null : Number(c.bbl),
      previous: p === undefined ? null : Number(p.bbl),
      currentBbl: c?.bbl ?? null,
      previousBbl: p?.bbl ?? null,
    });
  }
  return rows;
}

function TrendTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: Row }>;
}): React.JSX.Element | null {
  const row = payload?.[0]?.payload;
  if (active !== true || row === undefined) return null;
  return (
    <div
      style={{
        background: 'var(--color-bg-tertiary)',
        border: '1px solid var(--color-border-secondary)',
        borderRadius: '10px',
        padding: '8px 10px',
        fontSize: '12px',
        color: 'var(--color-text-primary)',
      }}
    >
      {row.currentDay !== null && (
        <div>
          {formatDay(row.currentDay)}: <strong>{formatBbl(row.currentBbl ?? '0.00')} BBL</strong>
        </div>
      )}
      {row.previousDay !== null && (
        <div style={{ color: 'var(--color-text-secondary)' }}>
          {formatDay(row.previousDay)}: {formatBbl(row.previousBbl ?? '0.00')} BBL
        </div>
      )}
    </div>
  );
}

export function VolumeTrendChart({
  current,
  previous,
}: {
  current: readonly TrendPoint[];
  previous: readonly TrendPoint[];
}): React.JSX.Element {
  const rows = overlay(current, previous);
  return (
    <div>
      <div style={{ width: '100%', height: 240 }} data-testid="volume-trend-chart">
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--color-border-primary)" vertical={false} />
            <XAxis
              dataKey="n"
              tickFormatter={(n: number): string => {
                const d = rows[n - 1]?.currentDay;
                return d === null || d === undefined ? '' : formatDay(d).replace(/, \d{4}$/, '');
              }}
              tick={{ fill: 'var(--color-text-tertiary)', fontSize: 11 }}
              stroke="var(--color-border-secondary)"
              minTickGap={16}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-tertiary)', fontSize: 11 }}
              stroke="var(--color-border-secondary)"
              width={56}
            />
            <Tooltip
              content={<TrendTooltip />}
              cursor={{ stroke: 'var(--color-border-secondary)' }}
            />
            <Legend
              formatter={(value: string): string =>
                value === 'current' ? 'This period' : 'Comparison period'
              }
            />
            <Line
              type="monotone"
              dataKey="current"
              stroke={CURRENT}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="previous"
              stroke={PREVIOUS}
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              activeDot={{ r: 4 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <details style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
        <summary style={{ cursor: 'pointer' }}>Show the numbers</summary>
        <table style={{ borderCollapse: 'collapse', marginTop: '6px' }} data-testid="trend-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Day</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>BBL</th>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Comparison day</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>BBL</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.n}>
                <td style={{ padding: '2px 8px' }}>
                  {r.currentDay === null ? '—' : formatDay(r.currentDay)}
                </td>
                <td style={{ padding: '2px 8px', textAlign: 'right' }}>
                  {r.currentBbl === null ? '—' : formatBbl(r.currentBbl)}
                </td>
                <td style={{ padding: '2px 8px' }}>
                  {r.previousDay === null ? '—' : formatDay(r.previousDay)}
                </td>
                <td style={{ padding: '2px 8px', textAlign: 'right' }}>
                  {r.previousBbl === null ? '—' : formatBbl(r.previousBbl)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
